import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Clock, ChevronRight, Search, Utensils } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountry } from "@/lib/country-context";
import { FoodImage } from "@/components/food-image";
import api from "@/lib/api";

const CUISINES = ["All", "Ghanaian", "Nigerian", "Nigerian Grills", "Ghanaian Street", "Nigerian Home"];

export default function RestaurantsPage() {
  const { formatPrice } = useCountry();
  const [search, setSearch] = useState("");
  const [cuisine, setCuisine] = useState("All");
  const [featured, setFeatured] = useState(null);
  const [restaurants, setRestaurants] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/restaurants/featured").then(({ data }) => setFeatured(data)).catch(() => setFeatured([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (cuisine && cuisine !== "All") params.cuisine = cuisine;
    api
      .get("/restaurants", { params })
      .then(({ data }) => setRestaurants(data))
      .catch(() => setRestaurants([]))
      .finally(() => setLoading(false));
  }, [search, cuisine]);

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b p-4 md:p-6">
        <h1 className="font-serif text-2xl font-bold mb-4">Restaurants</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search restaurants or cuisines..."
            className="pl-9 h-11 rounded-xl bg-card"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="restaurants-search-input"
          />
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
          {CUISINES.map((c) => (
            <button
              key={c}
              onClick={() => setCuisine(c)}
              data-testid={`restaurants-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                cuisine === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-8">
        {cuisine === "All" && !search && featured && featured.length > 0 && (
          <section>
            <h2 className="font-serif text-xl font-bold mb-4">Featured</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {featured.map((r) => (
                <RestaurantCard key={r.id} r={r} formatPrice={formatPrice} />
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-serif text-xl font-bold mb-4">
            {cuisine !== "All" || search ? "Results" : "All Restaurants"}
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : !restaurants || restaurants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Utensils className="h-12 w-12 mb-4 opacity-20" />
              <p className="font-medium">No restaurants found</p>
              <p className="text-sm mt-1">Try a different search or cuisine</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {restaurants.map((r) => (
                <RestaurantCard key={r.id} r={r} formatPrice={formatPrice} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function RestaurantCard({ r, formatPrice }) {
  return (
    <Link to={`/restaurants/${r.id}`} data-testid={`restaurant-card-${r.id}`}>
      <div className="group bg-card border rounded-2xl overflow-hidden hover:shadow-md transition-all hover:-translate-y-0.5 cursor-pointer h-full flex flex-col">
        <div className="h-44 bg-muted relative overflow-hidden">
          <FoodImage src={r.image_url} alt={r.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            iconClassName="h-12 w-12 text-muted-foreground/20" />
          {!r.is_open && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
              <Badge variant="secondary" className="text-sm font-medium">Closed</Badge>
            </div>
          )}
          {r.is_featured && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-primary/90 text-primary-foreground text-xs font-semibold">Featured</Badge>
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col gap-2 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-serif font-bold text-lg leading-tight">{r.name}</h3>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:text-primary transition-colors" />
          </div>
          <p className="text-sm text-muted-foreground">{r.cuisine}</p>
          <div className="flex items-center gap-4 text-sm mt-auto pt-2 border-t">
            <span className="flex items-center gap-1 text-amber-500 font-medium">
              <Star className="h-3.5 w-3.5 fill-amber-500" />
              {r.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {r.delivery_time} min
            </span>
            <span className="text-muted-foreground ml-auto">{formatPrice(r.delivery_fee)} delivery</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <Skeleton className="h-44 w-full" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-full" />
      </div>
    </div>
  );
}
